export const PAYMENT_METHOD_OPTIONS = [
  { value: "EFT", label: "EFT" },
  { value: "Cheque", label: "Cheque" },
  { value: "Virtual_Card", label: "Virtual Card" },
  { value: "ACH", label: "ACH" },
];

export const CLAIM_STATUS_OPTIONS = [
  // { value: "SUBMITTED", label: "Submitted" },
  // { value: "ACCEPTED", label: "Accepted" },
  // { value: "REJECTED", label: "Rejected" },
  // Denied / Paid / Partial / Pending / Adjusted
  // status as per ticket
  { value: "DENIED", label: "Denied" },
  { value: "PAID", label: "Paid" },
  { value: "PARTIAL", label: "Partial" },
  { value: "PENDING", label: "Pending" },
  { value: "ADJUSTED", label: "Adjusted" },
];

export const PAID_AS_OPTIONS = [
  { value: "PRIMARY", label: "Primary" },
  { value: "SECONDARY", label: "Secondary" },
  { value: "TERTIARY", label: "Tertiary" },
];

export const ADJUSTMENT_TYPE_OPTIONS = [
  { value: "Contract", label: "Contract" },
  { value: "Other", label: "Other" },
  { value: "writeOff", label: "Write-Off" },
  // { value: "Non-Covered", label: "Non-Covered" },
];

export const EOB_STATUS_VARIANT: Record<
  string,
  "default" | "warning" | "success" | "pending" | "error"
> = {
  POSTED: "default",
  PARTIAL: "warning",
  // The backend sends the partial state as PARTIALLY_POSTED; without this the
  // chip fell back to "default" and looked identical to a fully posted EOB.
  PARTIALLY_POSTED: "warning",
  FULL: "success",
  PENDING: "pending",
  FAILED: "error",
};
export type EraClaimDisplayStatus = "PAID" | "DENIED" | "POSTED";

// ECRH-4700 — CPT-level posting. Claim posting status derived from how many
// CPT lines have been posted. Prefer backend `postingStatus`, fall back to
// counting posted CPTs.
export type EraPostingDisplayStatus =
  "PAID" | "PARTIALLY_PAID" | "NOT_PAID" | "DENIED";

// ECRH-5170 — the claim header shows the POSTING status only (Not / Partially /
// Fully Posted), never the payer's adjudication status. Internal bucket keys are
// kept as-is so the EOB worklist (SelectedClaimsSection) is unaffected; only the
// user-facing labels reflect posting wording.
export const ERA_POSTING_STATUS_BADGE: Record<
  EraPostingDisplayStatus,
  { label: string; bg: string; color: string; border: string }
> = {
  PAID: {
    label: "Fully Posted",
    bg: "#E6F4EA",
    color: "#1E7A3C",
    border: "#1E7A3C",
  },
  PARTIALLY_PAID: {
    label: "Partially Posted",
    bg: "#FEF0C7",
    color: "#B54708",
    border: "#B54708",
  },
  NOT_PAID: {
    label: "Not Posted",
    bg: "#EEF0F2",
    color: "#475467",
    border: "#98A2B3",
  },
  DENIED: {
    label: "Denied",
    bg: "#FDECEC",
    color: "#B42318",
    border: "#B42318",
  },
};

// Normalises any backend posting-status string ("Not Paid", "Partially Paid",
// "Paid", "FULLY_PAID", "Denied"...) into one of our display buckets.
export const normalizeEraPostingStatus = (
  raw?: string | null
): EraPostingDisplayStatus | null => {
  if (!raw) return null;
  const s = String(raw)
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (s.includes("PARTIAL")) return "PARTIALLY_PAID";
  if (
    s === "PAID" ||
    s === "FULLY_PAID" ||
    s === "FULL" ||
    s === "FULLY_POSTED"
  )
    return "PAID";
  if (
    s === "NOT_PAID" ||
    s === "UNPAID" ||
    s === "PENDING" ||
    s === "NOT_POSTED"
  )
    return "NOT_PAID";
  if (s === "DENIED" || s === "REJECTED") return "DENIED";
  return null;
};

// Posting-status strings sent to the BE on a CPT post. ECRH-5170 — must match
// the posting-status vocabulary the get API returns ("Fully Posted" /
// "Partially Posted" / "Not Posted").
export const ERA_POSTING_STATUS_VALUE = {
  PAID: "Fully Posted",
  PARTIALLY_PAID: "Partially Posted",
  NOT_PAID: "Not Posted",
} as const;

export const VALID_DENIAL_REASON_GROUPS = ["CO", "PR", "OA", "PI"] as const;

export const ERA_PROCEDURE_COLUMNS: { key: string; label: string }[] = [
  { key: "cpt", label: "Procedure Code" },
  { key: "bill", label: "Bill ($)" },
  { key: "insurancePaid", label: "Insurance Paid ($)" },
  { key: "allowed", label: "Allowed ($)" },
  { key: "deduct", label: "Deduct ($)" },
  { key: "coInsurance", label: "Coinsurance ($)" },
  { key: "coPay", label: "CoPay ($)" },
  { key: "patientResp", label: "Patient Resp ($)" },
  { key: "contractAdj", label: "Contract Adj ($)" },
  { key: "otherAdj", label: "Other Adj ($)" },
  // Balance column hidden from ERA procedure tables.
  // { key: "balance", label: "Balance ($)" },
  { key: "status", label: "Status" },
];

// Statuses in which the whole EOB payment has been applied, so a zero
// `remainingPayment` from the backend is a real zero rather than "not started".
export const EOB_FULLY_POSTED_STATUSES = ["POSTED", "FULL"];

/**
 * Rem. Payment = Payment Amount − Posted Amount.
 *
 * The backend reports `remainingPayment` only once a payment has been posted,
 * so a pending EOB reads as 0 when the entire payment is still unapplied.
 */
export const getEobRemainingPayment = (eob: {
  totalPaymentAmount?: string | number | null;
  remainingPayment?: string | number | null;
  status?: string | null;
}): number => {
  const paymentAmount = Number(eob?.totalPaymentAmount) || 0;
  const reportedRemaining = Number(eob?.remainingPayment) || 0;

  if (reportedRemaining > 0) return reportedRemaining;

  const status = (eob?.status ?? "").toUpperCase();
  return EOB_FULLY_POSTED_STATUSES.includes(status) ? 0 : paymentAmount;
};
